/**
 * useSkerpaEntitySummary — Fetches project entity tree and builds a concise
 * summary for Skerpa's system prompt. Cached via React Query (5 min stale).
 * This is the knowledge graph data — single source of truth.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useProjectStore } from '@/stores/projectStore'
import { useAuthStore } from '@/stores/authStore'
import { entityTreeApi, type EntityTreeClassGroup } from '@/lib/api/client'
import { useEntityListener } from '@/hooks/useEntityListener'

export function useSkerpaEntitySummary(): string {
  const project = useProjectStore(s => s.currentProject)
  const account = useAuthStore(s => s.currentAccount)
  const queryClient = useQueryClient()

  const { data } = useQuery({
    queryKey: ['skerpaEntitySummary', account?.id, project?.id],
    queryFn: () => entityTreeApi.getTree(account!.id, project!.id),
    enabled: !!account?.id && !!project?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  })

  // Refresh entity tree on ANY entity change (from any source)
  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['skerpaEntitySummary'] })
  }, [queryClient])
  useEntityListener('entity-created', '*', refresh)
  useEntityListener('entity-updated', '*', refresh)
  useEntityListener('entity-deleted', '*', refresh)

  if (!data?.byDomain) return ''

  const lines: string[] = []

  for (const domain of ['market', 'business', 'solution'] as const) {
    const groups = data.byDomain[domain]
    if (!groups?.length) continue

    const domainEntities = groups.filter(g => g.instances.length > 0)
    if (!domainEntities.length) continue

    lines.push(`${domain.toUpperCase()} DOMAIN:`)
    for (const group of domainEntities) {
      const names = group.instances.slice(0, 15).map(i => i.label)
      const more = group.instances.length > 15 ? ` (+${group.instances.length - 15} more)` : ''
      lines.push(`  ${group.className} (${group.instances.length}): ${names.join(', ')}${more}`)
    }
  }

  if (!lines.length) return ''
  return `Total entities: ${data.entities.length}\n${lines.join('\n')}`
}
